import { AutoCompleteOption } from "../components/form/APAutoComplete";
import { Dossier } from "../types/Dossier";
import {
  ExpenseClaim,
  ExpenseClaimStatus,
  ExpenseClaimType,
} from "../types/ExpenseClaim";
import { isAfter, isBefore } from "./dates";

const expenseClaimStatusOrder = [
  "awaiting-approval",
  "approved",
  "ready-for-payment",
  "payment-submitted",
  "paid",
  "declined",
  "cancelled",
];

const isLinkedClaim = (claim: ExpenseClaim) => {
  return claim.status === "payment-submitted" && !!claim.transactionId;
};

export const calculatePendingClaimBalance = (
  pendingExpenseClaims: ExpenseClaim[]
) => {
  // Don't include payment-submitted claims that have a transactionId
  return pendingExpenseClaims.reduce(
    (acc, claim) => (isLinkedClaim(claim) ? acc : acc + claim.incGst),
    0
  );
};

export const filterExpenseClaims = (
  expenseClaims: ExpenseClaim[],
  dateFrom?: string,
  dateTo?: string,
  claimTypes?: ExpenseClaimType[],
  excludeLinkedClaims: boolean = false
) => {
  return expenseClaims.filter((claim) => {
    const dateFromCheck = dateFrom
      ? isAfter(claim.transactionDate, dateFrom)
      : true;
    const dateToCheck = dateTo ? isBefore(claim.transactionDate, dateTo) : true;
    const claimTypeCheck = claimTypes ? claimTypes.includes(claim.type) : true;
    const excludeLinkedClaimsCheck = excludeLinkedClaims
      ? !isLinkedClaim(claim)
      : true;

    return (
      dateFromCheck && dateToCheck && claimTypeCheck && excludeLinkedClaimsCheck
    );
  });
};

export const filterExpenseClaimTypeOptions = (
  options: AutoCompleteOption[],
  dossier: Dossier
) => {
  return options.filter((option) => {
    const hideFuel = dossier.powerPlantType === "bev" && option.id === "fuel";
    const hideCharging =
      !dossier.canClaimHomeCharging &&
      !dossier.canClaimPublicCharging &&
      option.id === "charging";

    return !hideFuel && !hideCharging;
  });
};

export const filterExpenseClaimChargingTypeOptions = (
  options: AutoCompleteOption[],
  dossier: Dossier
) => {
  return options.filter((option) => {
    const hideHomeCharging =
      !dossier.canClaimHomeCharging && option.id === "home-charging";
    const hidePublicCharging =
      !dossier.canClaimPublicCharging && option.id === "public-charging";

    return !hideHomeCharging && !hidePublicCharging;
  });
};

export const calculateHomeChargingIncGst = (
  lastOdometer: number,
  currentOdometer: number,
  homeChargingRate: number,
  percentageChargeAtHome: number
) => {
  const amount =
    (currentOdometer - lastOdometer) *
    homeChargingRate *
    (percentageChargeAtHome / 100);

  return parseFloat(amount.toFixed(2));
};

export const sortExpenseClaimsByStatus = (expenseClaims: ExpenseClaim[]) => {
  return expenseClaims.sort((a, b) => {
    return (
      expenseClaimStatusOrder.indexOf(a.status) -
      expenseClaimStatusOrder.indexOf(b.status)
    );
  });
};

export const isExpenseClaimCancelable = (
  status: ExpenseClaimStatus
): boolean => {
  return (
    status === "awaiting-approval" ||
    status === "approved" ||
    status === "ready-for-payment"
  );
};
